import { useNavigate, useSearch } from "@tanstack/react-router";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  FORECAST_TABS,
  type FinancialForecastSearch,
  type FinancialForecastTab,
} from "..";
import { CalendarTab } from "../components/CalendarTab";

const DEFAULT_TAB: FinancialForecastTab = "calendar";

/**
 * Financial Forecast shell.
 *
 * Tabbed from the start: the Calendar is the first surface, and the P&L,
 * runway and scenario views this feature is meant to carry land alongside it.
 * Tab state lives in the URL (`?tab=`) so a view is linkable, matching the
 * Cash Flow page. The default tab is omitted from the URL rather than written
 * to it.
 *
 * The tab value is clamped HERE rather than trusted from the route's
 * `validateSearch`: an unrecognised `?tab=` reaches the component anyway (the
 * other tabbed pages render an empty body when that happens), so a bad link
 * falls back to the default instead of showing a page with no content.
 */
export function FinancialForecastPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/financial-forecast" }) as FinancialForecastSearch;
  const requested = search.tab;
  const tab: FinancialForecastTab = FORECAST_TABS.includes(
    requested as FinancialForecastTab,
  )
    ? (requested as FinancialForecastTab)
    : DEFAULT_TAB;

  const setTab = (next: FinancialForecastTab) =>
    navigate({
      to: "/financial-forecast",
      search: (prev: FinancialForecastSearch) => ({
        ...prev,
        tab: next === DEFAULT_TAB ? undefined : next,
      }),
    });

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Financial Forecast</h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          What we believe will happen, as dated facts — and what it does to the
          numbers. Cash timing is one projection of these assumptions; P&amp;L and
          ARR are others.
        </p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as FinancialForecastTab)}>
        <TabsList variant="underline">
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
        </TabsList>
        <TabsContent value="calendar" className="pt-4">
          <CalendarTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
